/**
 * Hiring-demand board — grouping roles and people into Department → Unit → Team.
 *
 * Split from demandModel.ts at the 500-line ceiling, and the seam is real: this
 * file decides WHERE a row sits, demandModel.ts does the arithmetic drawn across
 * it. `demandModel.ts` re-exports everything here, so importers do not have to
 * know which half a name lives in.
 */
import type { BoardRole, DemandBoard, RosterMember } from '../../../types/taDemand';

/**
 * The third level of the tree: the TEAM, as the headcount plan names it.
 *
 * The HDB has no team column, so `role.team` is resolved on the SERVER — the
 * job-family map in taDemandTeams.js, plus whatever leads have moved by hand
 * (taDemandOrg.js holds the chart the names come from). It briefly grouped by
 * job family instead, because that was the only thing the HDB could supply; the
 * structure Theresa plans in is Department → Unit → Team, so this is that.
 *
 * `fromChart` marks a team the org chart lists. Those render whether or not
 * anything is planned for them — an empty team is a real answer ("we are asking
 * for nobody here") and, just as importantly, it is somewhere to drop a role.
 */
export interface TeamNode {
  key: string;
  name: string;
  roles: BoardRole[];
  /** The seats that already exist in this team, by job title. */
  members: RosterMember[];
  fromChart: boolean;
}
export interface UnitNode {
  key: string;
  name: string;
  owner: string | null;
  teams: TeamNode[];
  /** Roles whose team is blank, or names a team no longer in the org chart. */
  looseRoles: BoardRole[];
  /** The seats that already exist here. */
  members: RosterMember[];
  /**
   * Approved, then dropped. Shown collapsed under the unit and counted in
   * NOTHING — they are deliberately absent from `teams`/`looseRoles`, which is
   * what every demand figure is summed from.
   */
  cancelled?: BoardRole[];
}
export interface DeptNode {
  key: string;
  name: string;
  owner: string | null;
  units: UnitNode[];
}


/**
 * Group roles into the org tree.
 *
 * Two things this deliberately does NOT do. It does not drop a role whose
 * department or unit is unknown — those surface under an "Unassigned" bucket,
 * because a role invisible on the board is a role nobody plans for, and the HDB's
 * spelling of a unit will not always match the org chart's. And it does not
 * invent teams: a role with no team, or a team the chart no longer lists, sits
 * directly on its unit as a loose role.
 */
export function buildTree(board: DemandBoard): DeptNode[] {
  const roles: BoardRole[] = [...board.hdb, ...board.drafts];
  const unplaced = new Set(roles);
  /*
    ONE PERSON, ONE ROW. Somebody hired through a role inside this window is
    shown on that role's row — the seat and the colleague are the same thing.
    Dropping them here rather than in the roster component is deliberate: a unit's
    current-team FTE is summed from this list, and leaving them in would count
    the seat twice, once as a hire and once as a head.
  */
  const allRoster = (board.roster ?? []).filter((m) => !m.onRoleRow);
  /*
    Cancelled roles ride ALONGSIDE the tree's live roles, never inside them.
    They hang off the unit they belonged to so a lead can open them, and they
    reach no `roles` array — everything that counts the board counts through
    those, and a dropped seat must not appear in a demand figure.
  */
  const allCancelled = board.cancelled ?? [];
  // Claim-tracked like everything else, so a cancelled role whose unit spelling
  // the chart does not know still lands somewhere a lead can see it rather than
  // being dropped on the floor — the one bucket nobody ever looks in.
  const placedCancelled = new Set();

  const nodes: DeptNode[] = board.structure.map((dept) => {
    const deptKeys = keysFor(dept.name, dept.aliases);
    const mine = roles.filter((r) => matches(r.department, deptKeys));
    const roster = allRoster.filter((m) => matches(m.department, deptKeys));
    mine.forEach((r) => unplaced.delete(r));

    const claimedByUnit = new Set<BoardRole>();
    // People are claim-tracked exactly like roles. Without it a unit name that
    // resolves to two chart units listed the same colleague under both — and
    // counted their FTE twice in each unit's "current team".
    const claimedMembers = new Set<RosterMember>();
    const units: UnitNode[] = dept.units.map((unit) => {
      const unitKeys = keysFor(unit.name, unit.aliases);
      const ours = mine.filter((r) => !claimedByUnit.has(r) && matches(r.unit, unitKeys));
      ours.forEach((r) => claimedByUnit.add(r));

      const members = roster.filter((m) => !claimedMembers.has(m) && matches(m.unit, unitKeys));
      members.forEach((m) => claimedMembers.add(m));
      const dropped = allCancelled.filter(
        (r) => !placedCancelled.has(r) && matches(r.department, deptKeys) && matches(r.unit, unitKeys),
      );
      dropped.forEach((r) => placedCancelled.add(r));

      /*
        The chart's own teams come FIRST and in the chart's order — that order is
        the org's, not ours, and sorting it alphabetically would make the board
        stop reading like the headcount plan. Then teams that only the data or a
        lead knows about, alphabetically, so a name the chart has not caught up
        with is still visible rather than dropped.
      */
      const chartTeams = unit.teams ?? [];
      const extra = board.extraTeams?.[`${dept.name}|${unit.name}`] ?? [];
      const known = new Set(chartTeams.map(norm));
      const others = dedupe([...ours.map(teamOf).filter(Boolean), ...extra])
        .filter((t) => !known.has(norm(t)))
        .sort((a, b) => a.localeCompare(b));

      const teams: TeamNode[] = [...chartTeams, ...others].map((name) => ({
        key: `${dept.name}|${unit.name}|${name}`,
        name,
        roles: ours.filter((r) => norm(teamOf(r)) === norm(name)),
        members: members.filter((m) => norm(m.team) === norm(name)),
        fromChart: known.has(norm(name)),
      }));
      const claimed = new Set([...chartTeams, ...others].map(norm));
      const node: UnitNode = {
        key: `${dept.name}|${unit.name}`,
        name: unit.name,
        owner: unit.owner,
        teams,
        looseRoles: ours.filter((r) => !claimed.has(norm(teamOf(r)))),
        members: members.filter((m) => !claimed.has(norm(m.team))),
        cancelled: dropped,
      };
      return collapseSoleTeam(node);
    });

    // A role whose DEPARTMENT is recognised but whose unit is not stays inside
    // its department rather than falling to the bottom of the board. The
    // department is the reliable join (the HDB and the chart agree on those six
    // names); unit spelling is where they diverge, and a role parked in a global
    // bucket is one nobody plans for.
    const noUnit = mine.filter((r) => !claimedByUnit.has(r));
    // A PERSON whose unit the chart does not recognise needs the same net. They
    // had none: roles fell into this bucket and people were simply filtered out,
    // so a colleague disappeared from the board and the unit's "current team"
    // reported less FTE than it really has. The department is the reliable
    // join; unit spelling is exactly where the HDB, the people database and the
    // chart diverge.
    const noUnitMembers = roster.filter((m) => !claimedMembers.has(m));
    const noUnitCancelled = allCancelled.filter(
      (r) => !placedCancelled.has(r) && matches(r.department, deptKeys),
    );
    noUnitCancelled.forEach((r) => placedCancelled.add(r));
    if (noUnit.length || noUnitMembers.length || noUnitCancelled.length) {
      units.push({
        key: `${dept.name}|__unmapped__`,
        name: `Unit not recognised — ${listUnitNames(noUnit, noUnitMembers)}`,
        owner: null,
        teams: [],
        looseRoles: noUnit,
        members: noUnitMembers,
        cancelled: noUnitCancelled,
      });
    }
    return { key: dept.name, name: dept.name, owner: dept.owner, units };
  });

  // Only a role whose DEPARTMENT could not be matched ends up here.
  const orphans = [...unplaced];
  if (orphans.length) {
    const byKey = new Map<string, BoardRole[]>();
    for (const r of orphans) {
      const k = `${norm(r.department)}|${norm(r.unit)}`;
      const list = byKey.get(k);
      if (list) list.push(r);
      else byKey.set(k, [r]);
    }
    nodes.push({
      key: '__unassigned__',
      name: 'Department not recognised',
      owner: null,
      units: [...byKey.entries()].map(([k, v]) => ({
        key: `__unassigned__|${k}`,
        name: labelFor(k, v),
        owner: null,
        teams: [],
        looseRoles: v,
        members: [],
      })),
    });
  }
  return nodes;
}

/**
 * A unit whose ONLY team is itself is one level, not two.
 *
 * Talent Acquisition is a unit with a single team called Talent Acquisition, and
 * the board rendered both — so reading it meant clicking "Talent Acquisition",
 * then "Talent Acquisition" again, and choosing between an "Add role" and an
 * "Add role directly to Talent Acquisition" that did exactly the same thing. The
 * second level carried no information, and two buttons for one action is a
 * question the reader has to answer before they can act.
 *
 * Only when the names MATCH and it is the unit's sole team. A unit with one team
 * called something else is a real distinction and keeps both levels — the chart
 * is saying the unit expects more teams than it has today.
 */
function collapseSoleTeam(u: UnitNode): UnitNode {
  if (u.teams.length !== 1 || norm(u.teams[0].name) !== norm(u.name)) return u;
  const [only] = u.teams;
  return {
    ...u,
    teams: [],
    looseRoles: [...only.roles, ...u.looseRoles],
    members: [...only.members, ...u.members],
  };
}

/**
 * Which team a row sits in — the ONE field, for an HDB row and a draft alike.
 *
 * Deliberately nothing else. It used to fall back to a draft's own title, which
 * minted a brand-new bucket named after the role and sorted it alphabetically
 * into some unrelated part of the unit: the "I added a role and it vanished"
 * report. A row with no team stays loose at the top of its unit, which is where
 * the "Add role" button that created it is — and which is now a labelled drop
 * target rather than an unexplained gap.
 */
export function teamOf(r: BoardRole): string {
  return (r.team || '').trim();
}

/**
 * Is this a unit somebody actually owns?
 *
 * Two of the nodes on this tree are DIAGNOSTICS, not units: `__unmapped__` (a
 * department's rows whose unit the chart does not know) and the `__unassigned__`
 * department's children (rows whose department it does not know either). Their
 * names are built from whatever the source system wrote, so submitting one or
 * adding a team to one would send a synthetic name to the store and create a
 * record for a unit that does not exist. The controls are hidden on both.
 */
export const isRealUnit = (u: UnitNode): boolean =>
  !u.key.includes('__unmapped__') && !u.key.startsWith('__unassigned__');

/** The teams a role could be moved into, in the order the picker offers them. */
export function teamChoices(u: UnitNode): string[] {
  return u.teams.map((t) => t.name);
}

const norm = (s: string | undefined | null) =>
  (s ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Drop a leading "<something> - " qualifier. The headcount plan writes units as
 * "Ops - Customer Ops" and "Finance - Accounting & Controlling"; the org chart
 * writes the part after the dash.
 */
const stripPrefix = (s: string) => s.replace(/^[^-—]{1,24}[-—]\s*/, '').trim();

/** Every spelling that should resolve to one node, normalised. */
function keysFor(name: string, aliases: string[] | undefined): Set<string> {
  const out = new Set<string>();
  for (const raw of [name, ...(aliases ?? [])]) {
    if (!raw) continue;
    out.add(norm(raw));
    out.add(norm(stripPrefix(raw)));
  }
  out.delete('');
  return out;
}

/** Does this HDB value name the node? Compared bare and prefix-stripped. */
function matches(value: string | undefined, keys: Set<string>): boolean {
  const v = norm(value);
  if (!v) return false;
  return keys.has(v) || keys.has(norm(stripPrefix(value ?? '')));
}

const dedupe = (xs: string[]) => [...new Map(xs.map((x) => [norm(x), x])).values()];

/** Name the bucket after what the source systems actually call those units. */
function listUnitNames(roles: BoardRole[], members: RosterMember[] = []): string {
  const names = [
    ...new Set([...roles, ...members].map((r) => r.unit?.trim()).filter(Boolean)),
  ];
  if (!names.length) return 'no unit set';
  return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

function labelFor(key: string, roles: BoardRole[]): string {
  const r = roles[0];
  const dept = r.department?.trim();
  const unit = r.unit?.trim();
  if (dept && unit) return `${dept} — ${unit}`;
  return dept || unit || key.replace('|', ' — ') || 'Unknown';
}

export const rolesOfUnit = (u: UnitNode): BoardRole[] =>
  u.teams.flatMap((t) => t.roles).concat(u.looseRoles);
export const rolesOfDept = (d: DeptNode): BoardRole[] => d.units.flatMap(rolesOfUnit);

